/**
 * Las piezas de los formularios de ajustes que comparten varias pantallas: el
 * interruptor con su explicación y la «i» que explica un campo.
 *
 * Vivían en la pantalla de ajustes de página del panel, que ya no existe (esos
 * ajustes se editan en el editor); se quedaron aquí porque los usan la pestaña
 * «Página» de una oferta, las páginas del catálogo y el editor del blog.
 */
import { useEffect, useRef, useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'

/**
 * Un interruptor con su nombre y su explicación.
 *
 * Lo usa la pestaña «Página» de una oferta: son la
 * misma decisión —indexar, salir en el sitemap, atar la vista previa al SEO—
 * sobre una página que no vive en el editor. Dibujarla otra vez daría dos
 * interruptores distintos para lo mismo. Ver `OfferPageSettings`.
 */
export function ToggleRow({
  icon,
  checked,
  onChange,
  title,
  description,
}: {
  icon: React.ReactNode
  checked: boolean
  onChange: (value: boolean) => void
  title: string
  description: string
}) {
  return (
    <div className="dash-page-settings-toggle">
      <span className="dash-page-settings-toggle-icon">{icon}</span>
      <div>
        <strong>{title}</strong>
        <small>{description}</small>
      </div>
      <button
        type="button"
        className={`dash-page-settings-switch${checked ? ' is-on' : ''}`}
        aria-pressed={checked}
        aria-label={title}
        onClick={() => onChange(!checked)}
      >
        <i />
      </button>
    </div>
  )
}

/** Small "i" next to a field label; shows an explanation on hover. */
/**
 * La «i» que explica un campo.
 *
 * Era un `<span title=…>`, y así no se podía leer: el globo lo pinta el
 * navegador tras casi un segundo parado encima, al pulsarla no ocurría nada y,
 * como vive dentro de un `<label>`, el clic se lo llevaba el campo —quien la
 * pulsaba veía el cursor metiéndose en el `input` y ninguna explicación—. En un
 * teléfono, donde no existe «pasar por encima», no había forma de abrirla.
 *
 * Ahora es un botón con el globo de la casa —el mismo `data-tooltip` negro que
 * llevan los iconos del panel—: sale al pasar, al tabular y al pulsar, y se
 * cierra con Escape, pulsándola otra vez o picando fuera.
 */
export function InfoHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <button
      ref={ref}
      type="button"
      className={`dash-info-hint${open ? ' is-open' : ''}`}
      data-tooltip={text}
      aria-label={text}
      /* Sin frenar el clic, el `<label>` que la contiene lo entiende como suyo:
         enfoca el campo y de paso se lleva el globo por delante. */
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        setOpen((value) => !value)
      }}
    >
      <IconInfoCircle size={12} />
    </button>
  )
}
